import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Send,
  Shield,
  AlertTriangle,
  Bot,
  Check,
  X,
  Loader2,
  Coins,
  Globe2,
  Plus,
  ExternalLink,
  Hash,
  Hourglass,
  HelpCircle as HelpCircleIcon,
  RefreshCw,
  SearchX,
} from "lucide-react";
import { toast } from "sonner";
import LiveTicker from "../components/aegis/LiveTicker";
import DisputeBtn from "../components/sentinel/DisputeButton";
import { useAuth } from "../contexts/AuthContext";
import {
  createSubmission,
  getMySubmissions,
  getSubmission,
  getMembership,
  getPendingSubmissions,
  verifySubmission,
  regroundSubmission,
  setAtomVerdict,
} from "../lib/api";

function formatApt(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000) return `${(x / 1_000).toFixed(2)}k`;
  return x.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Every status doubles as a *training-data label* — once a submission
// settles, its (claim, verdict, sources) tuple joins the public dataset
// that teaches downstream AI models what humans + LLMs agreed was true,
// false, disputed, or unverifiable. Tooltips below spell that out so
// public users understand the badges aren't decorative.
const STATUS_MAP = {
  triangulating: {
    label: "Triangulating",
    className: "border-amber-500/40 text-amber-300 bg-amber-500/5",
    Icon: Hourglass,
    tip: "Two independent LLMs (Claude + Gemini) are cross-checking this claim against live Google grounding. Please wait ~10s.",
  },
  consensus: {
    label: "AI Consensus",
    className: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
    Icon: Check,
    tip: "Both LLMs independently agreed on the verdict. This tuple (claim → verdict → sources) becomes a labelled example in the open training dataset that teaches future AI models historical fact-patterns.",
  },
  contested: {
    label: "Contested",
    className: "border-rose-500/40 text-rose-300 bg-rose-500/5",
    Icon: AlertTriangle,
    tip: "The two LLMs disagreed. Open for a human bounty — anyone can settle it with evidence.",
  },
  unresolved: {
    label: "Unresolved",
    className: "border-zinc-700 text-zinc-400 bg-zinc-900",
    Icon: AlertTriangle,
    tip: "Live grounding couldn't return a confident source. The claim is parked as unresolved and stays in the training set as an explicit \"insufficient evidence\" label — teaching AI when to withhold a verdict instead of hallucinating one.",
  },
  verified: {
    label: "Architect-Verified",
    className: "border-amber-500/60 text-amber-200 bg-amber-500/10",
    Icon: Shield,
    tip: "A human Architect reviewed the AI consensus and countersigned it. Architect-verified rows carry the highest weight in the training dataset — they are the gold-standard supervision signal.",
  },
  rejected: {
    label: "Rejected",
    className: "border-zinc-700 text-zinc-500 bg-zinc-900/50",
    Icon: X,
    tip: "Architect rejected the submission (spam, malformed, or out-of-scope). Not eligible for minting or chain anchoring.",
  },
  opinion: {
    label: "Opinion · Unverifiable",
    className: "border-violet-500/40 text-violet-300 bg-violet-500/5",
    Icon: HelpCircleIcon,
    tip: "Architect flagged this as personal opinion, not a factual claim. No mint, no chain anchor — but it stays in the dataset as an \"unverifiable\" label to teach AI the difference between fact and preference.",
  },
};

function StatusPill({ status }) {
  const m = STATUS_MAP[status] || STATUS_MAP.unresolved;
  const Icon = m.Icon;
  return (
    <span
      data-testid={`submission-status-${status}`}
      title={m.tip}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] font-mono border cursor-help ${m.className}`}
    >
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

function StatusLegend() {
  const rows = ["consensus", "verified", "unresolved"];
  return (
    <details
      data-testid="status-legend"
      className="border border-zinc-800 bg-[#0b0b0d] mb-3 group"
    >
      <summary className="cursor-pointer list-none px-3 py-2 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 font-mono flex items-center gap-2">
        <HelpCircleIcon className="h-3 w-3" />
        What do these statuses mean?
        <span className="ml-auto text-zinc-600 group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="border-t border-zinc-900 px-3 py-3 space-y-2 text-[11px] text-zinc-400 leading-relaxed font-mono">
        <p className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] mb-2">
          Every settled row becomes a labelled example in an open training
          dataset that teaches future AI models historical fact-patterns.
        </p>
        {rows.map((k) => {
          const m = STATUS_MAP[k];
          const Icon = m.Icon;
          return (
            <div key={k} className="flex items-start gap-2">
              <span
                className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] border ${m.className}`}
              >
                <Icon className="h-3 w-3" /> {m.label}
              </span>
              <span className="text-zinc-400">{m.tip}</span>
            </div>
          );
        })}
      </div>
    </details>
  );
}

function TriangulationCard({ tri, isArchitect, onReground, regrounding }) {
  if (!tri) return null;
  const { claude, gemini, grounding, status, consensus } = tri;
  const sources = grounding?.sources || [];
  const hasGroundingPayload =
    !!grounding && (grounding.used_search || grounding.summary || sources.length > 0 || grounding.error);
  return (
    <div className="border border-zinc-800 bg-black/40 mt-3 p-3 text-[11px] font-mono text-zinc-300 space-y-2">
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
        <Bot className="h-3 w-3" />
        {isArchitect ? "Cross-LLM triangulation" : "Sentinel verdict"}
        <span
          className={`ml-auto px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] border ${
            consensus ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"
          }`}
        >
          {status}
        </span>
      </div>

      {/* Per-model verdict blocks are architect-only. Public users see the
          Sentinel's synthesized voice (grounding summary + sources) instead
          of raw Claude/Gemini responses. */}
      {isArchitect && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <VerdictBlock label="Claude Sonnet 4.5" v={claude} accent="amber" />
          <VerdictBlock label="Gemini 2.5 Flash" v={gemini} accent="sky" />
        </div>
      )}

      {/* Grounding panel — always rendered for the Architect so they can
          see whether live web sources were fetched, and re-run grounding
          when it failed. Hidden for the public/submitter view only when
          there is literally nothing useful to display. */}
      {(hasGroundingPayload || isArchitect) && (
        <div className="border-t border-zinc-800 pt-2 mt-2">
          <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1 flex items-center gap-1.5 flex-wrap">
            <Globe2 className="h-3 w-3" /> Google grounding
            <span
              className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] border ${
                sources.length > 0
                  ? "border-emerald-500/40 text-emerald-300"
                  : grounding?.error
                  ? "border-rose-500/40 text-rose-300"
                  : "border-zinc-700 text-zinc-500"
              }`}
              data-testid="grounding-source-count"
            >
              {sources.length} source{sources.length === 1 ? "" : "s"}
            </span>
            {isArchitect && onReground && (
              <button
                type="button"
                onClick={onReground}
                disabled={regrounding}
                data-testid="architect-reground-btn"
                className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-40"
                title="Re-run Gemini grounded web search to fetch fresh citations"
              >
                <RefreshCw className={`h-3 w-3 ${regrounding ? "animate-spin" : ""}`} />
                {regrounding ? "fetching…" : "re-run grounding"}
              </button>
            )}
          </div>
          {grounding?.summary && (
            <div className="border-l-2 border-emerald-500/40 pl-3 mb-3">
              <div className="text-[9px] uppercase tracking-[0.3em] text-emerald-400/80 mb-1">
                Sentinel · cross-referenced
              </div>
              <div className="text-[12px] text-zinc-200 leading-relaxed">
                {grounding.summary}
              </div>
            </div>
          )}
          {sources.length > 0 ? (
            <ul className="space-y-1">
              {sources.map((s, i) => (
                <li key={i}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`grounding-source-${i}`}
                    className="inline-flex items-center gap-1 text-amber-300/80 hover:text-amber-200 text-[10px] truncate"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">{s.title || s.url}</span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div
              data-testid="grounding-empty-state"
              className="flex items-start gap-2 text-[10px] text-zinc-500 border border-dashed border-zinc-800 bg-black/30 px-2 py-1.5"
            >
              <SearchX className="h-3 w-3 mt-0.5 shrink-0 text-zinc-600" />
              <span className="leading-snug">
                {grounding?.error
                  ? `No live web sources — ${grounding.error}`
                  : "No live web sources fetched (Gemini search returned no citations)."}
                {isArchitect && " Tap re-run grounding to retry."}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AtomKindBadge({ kind }) {
  const map = {
    verifiable: { label: "verifiable", className: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" },
    unprovable: { label: "needs proof", className: "border-amber-500/40 text-amber-300 bg-amber-500/5" },
    opinion: { label: "opinion", className: "border-violet-500/40 text-violet-300 bg-violet-500/5" },
    harmful: { label: "harmful", className: "border-rose-500/50 text-rose-300 bg-rose-500/10" },
  };
  const m = map[kind] || map.verifiable;
  return (
    <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border font-mono ${m.className}`}>
      {m.label}
    </span>
  );
}

function AtomArchitectStatusBadge({ status }) {
  if (!status || status === "pending") return null;
  const map = {
    confirmed: { label: "confirmed", className: "border-emerald-500/60 text-emerald-200 bg-emerald-500/10" },
    rejected:  { label: "rejected",  className: "border-rose-500/60 text-rose-300 bg-rose-500/10" },
    needs_proof: { label: "bounty open", className: "border-sky-500/50 text-sky-300 bg-sky-500/10" },
  };
  const m = map[status] || { label: status, className: "border-zinc-700 text-zinc-400" };
  return (
    <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border font-mono ${m.className}`}>
      {m.label}
    </span>
  );
}

function AtomList({ atoms, submissionId, isArchitect, onAtomChange }) {
  const [busyAtomId, setBusyAtomId] = useState(null);
  if (!atoms || !atoms.length) return null;
  const verifiable = atoms.filter((a) => a.kind === "verifiable");
  const confirmed = verifiable.filter((a) => a.architect_verdict === "confirmed").length;
  const rejected  = verifiable.filter((a) => a.architect_verdict === "rejected").length;
  const bounty    = atoms.filter((a) => a.kind === "unprovable" || a.architect_verdict === "needs_proof").length;
  const totalV    = verifiable.length;
  const mintWeight = totalV ? Math.max(confirmed, verifiable.filter((a) => a.architect_verdict === "pending").length + confirmed) / totalV : 1;

  const setVerdict = async (atomId, verdict) => {
    setBusyAtomId(atomId);
    try {
      const res = await setAtomVerdict(submissionId, atomId, verdict);
      onAtomChange?.(res.atoms);
      toast.success(`Atom marked ${verdict.replace("_", " ")}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Atom update failed");
    } finally {
      setBusyAtomId(null);
    }
  };

  return (
    <div
      data-testid={`atom-list-${submissionId}`}
      className="border border-zinc-800 bg-black/40 mt-3 p-3 text-[11px] font-mono text-zinc-300 space-y-2"
    >
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2 flex-wrap">
        <Sparkles className="h-3 w-3" /> Atomic sub-claims
        <span className="ml-auto text-[9px] text-zinc-500">
          {totalV > 0 && <>{confirmed}/{totalV} verified · </>}
          {bounty > 0 && <>{bounty} bounty · </>}
          {rejected > 0 && <>{rejected} rejected · </>}
          mint scale {Math.round(mintWeight * 100)}%
        </span>
      </div>
      <ol className="space-y-2 list-none">
        {atoms.map((atom, idx) => {
          const verdictColor =
            atom.verdict === "true" ? "text-emerald-300"
            : atom.verdict === "false" ? "text-rose-300"
            : "text-zinc-400";
          return (
            <li
              key={atom.id}
              data-testid={`atom-${atom.id}`}
              className="border border-zinc-900 bg-black/30 p-2"
            >
              <div className="flex items-start gap-2 flex-wrap">
                <span className="text-[10px] font-mono text-zinc-600 shrink-0">#{idx + 1}</span>
                <span className="text-[11px] text-zinc-200 flex-1 leading-snug">{atom.text}</span>
                <AtomKindBadge kind={atom.kind} />
                <AtomArchitectStatusBadge status={atom.architect_verdict} />
              </div>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <span className={`uppercase text-[10px] tracking-[0.2em] ${verdictColor}`}>
                  AI: {atom.verdict || "—"}
                </span>
                <span className="text-[9px] text-zinc-500">
                  {Math.round((atom.confidence || 0) * 100)}% conf
                </span>
                {atom.reasoning && (
                  <span className="text-[10px] text-zinc-500 italic line-clamp-2 flex-1">
                    {atom.reasoning}
                  </span>
                )}
              </div>
              {isArchitect && (
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  <button
                    type="button"
                    disabled={busyAtomId === atom.id}
                    onClick={() => setVerdict(atom.id, "confirmed")}
                    data-testid={`atom-confirm-${atom.id}`}
                    className="px-2 py-1 text-[9px] uppercase tracking-[0.25em] border border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/15 disabled:opacity-40 font-mono inline-flex items-center gap-1"
                  >
                    <Check className="h-3 w-3" /> confirm
                  </button>
                  <button
                    type="button"
                    disabled={busyAtomId === atom.id}
                    onClick={() => setVerdict(atom.id, "rejected")}
                    data-testid={`atom-reject-${atom.id}`}
                    className="px-2 py-1 text-[9px] uppercase tracking-[0.25em] border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40 font-mono inline-flex items-center gap-1"
                  >
                    <X className="h-3 w-3" /> reject
                  </button>
                  <button
                    type="button"
                    disabled={busyAtomId === atom.id}
                    onClick={() => setVerdict(atom.id, "needs_proof")}
                    data-testid={`atom-bounty-${atom.id}`}
                    title="Open a Proof Bounty — other users can submit evidence"
                    className="px-2 py-1 text-[9px] uppercase tracking-[0.25em] border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-40 font-mono inline-flex items-center gap-1"
                  >
                    <HelpCircleIcon className="h-3 w-3" /> needs proof
                  </button>
                  <button
                    type="button"
                    disabled={busyAtomId === atom.id || atom.architect_verdict === "pending"}
                    onClick={() => setVerdict(atom.id, "pending")}
                    data-testid={`atom-reset-${atom.id}`}
                    className="px-2 py-1 text-[9px] uppercase tracking-[0.25em] border border-zinc-700 text-zinc-400 hover:bg-zinc-800/60 disabled:opacity-40 font-mono inline-flex items-center gap-1"
                  >
                    reset
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}


function VerdictBlock({ label, v, accent }) {
  const safe = v || {};
  const verdictColor =
    safe.verdict === "true"
      ? "text-emerald-300"
      : safe.verdict === "false"
      ? "text-rose-300"
      : "text-zinc-400";
  const accentClass = accent === "amber" ? "border-amber-500/30" : "border-sky-500/30";
  return (
    <div className={`border ${accentClass} bg-black/40 p-2`}>
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`font-mono uppercase text-[12px] tracking-[0.2em] ${verdictColor}`}>
          {safe.verdict || "—"}
        </span>
        <span className="text-[9px] text-zinc-500 font-mono">
          {Math.round((safe.confidence || 0) * 100)}% conf
        </span>
      </div>
      <div className="text-[10px] text-zinc-400 mt-1 leading-snug line-clamp-3">
        {safe.reasoning || "—"}
      </div>
    </div>
  );
}

function SubmissionRow({ submission, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [polling, setPolling] = useState(submission.status === "triangulating");

  useEffect(() => {
    if (submission.status !== "triangulating") {
      setPolling(false);
      return;
    }
    setPolling(true);
    const iv = setInterval(async () => {
      try {
        const res = await getSubmission(submission.id);
        if (res.submission?.status !== "triangulating") {
          clearInterval(iv);
          onRefresh?.();
        }
      } catch {
        // swallow; next poll will retry
      }
    }, 3500);
    return () => clearInterval(iv);
  }, [submission.id, submission.status, onRefresh]);

  const apt = submission.mint_result
    ? (submission.mint_result.coins || []).reduce(
        (sum, c) =>
          c.allocation_slice === "verifier_payout_to_submitter" ||
          c.allocation_slice === "verifier_payout" ||
          c.allocation_slice === "corroboration_payout"
            ? sum + Number(c.value_in_apt || 0)
            : sum,
        0
      )
    : 0;
  const isCorroboration = Boolean(submission.corroboration_reward);
  const isDupeOnly =
    Boolean(submission.duplicate_of_existing_citation) && !isCorroboration;

  return (
    <article
      data-testid={`submission-row-${submission.id}`}
      className="border border-zinc-800 bg-[#0b0b0d] hover:border-amber-500/40 transition-colors"
    >
      <button
        type="button"
        className="w-full text-left p-3 sm:p-4"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex flex-wrap items-start gap-2 mb-1">
          <span
            className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border font-mono ${
              submission.claim_type === "truth"
                ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
                : "border-rose-500/40 text-rose-300 bg-rose-500/5"
            }`}
          >
            claims · {submission.claim_type}
          </span>
          <StatusPill status={submission.status} />
          {polling && (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] text-amber-400 font-mono">
              <Loader2 className="h-3 w-3 animate-spin" /> polling
            </span>
          )}
          <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-mono">
            {new Date(submission.created_at).toLocaleString()}
          </span>
        </div>
        <div className="text-sm text-zinc-200 leading-snug">
          {submission.claim}
        </div>
        {submission.status === "verified" && apt > 0 && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 border border-amber-500/40 bg-amber-500/5 text-amber-300 text-[10px] uppercase tracking-[0.25em] font-mono">
            <Coins className="h-3 w-3" />
            {isCorroboration
              ? `+${formatApt(apt)} APT · corroboration reward`
              : `+${formatApt(apt)} APT minted to you`}
          </div>
        )}
        {submission.status === "verified" && apt === 0 && isDupeOnly && (
          <div
            data-testid={`submission-duplicate-${submission.id}`}
            className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 border border-zinc-700 bg-zinc-900/50 text-zinc-400 text-[10px] uppercase tracking-[0.25em] font-mono"
            title="You already submitted this fact — it stays linked but no additional AEGIS is minted."
          >
            <HelpCircleIcon className="h-3 w-3" /> already yours · no re-mint
          </div>
        )}
      </button>
      {expanded && (
        <div className="border-t border-zinc-900 px-3 sm:px-4 py-3 space-y-2">
          {submission.note && (
            <div className="text-[11px] text-zinc-400 italic">&ldquo;{submission.note}&rdquo;</div>
          )}
          {submission.sources && submission.sources.length > 0 && (
            <div className="text-[10px] font-mono text-zinc-500">
              sources:{" "}
              {submission.sources.map((s, i) => (
                <a
                  key={i}
                  href={s.startsWith("http") ? s : `https://${s}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-amber-300/80 hover:text-amber-200 mr-2"
                >
                  [{i + 1}]
                </a>
              ))}
            </div>
          )}
          {submission.triangulation && (
            <TriangulationCard tri={submission.triangulation} />
          )}
          {(submission.atoms || []).length > 0 && (
            <AtomList
              atoms={submission.atoms}
              submissionId={submission.id}
              isArchitect={false}
            />
          )}
          {submission.truth_block_id ? (
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono">
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-emerald-500/50 text-emerald-300 bg-emerald-500/[0.05] uppercase tracking-[0.22em]"
                data-testid={`submission-block-pill-${submission.id}`}
              >
                <Hash className="h-3 w-3" /> in block #{submission.truth_block_height}
              </span>
              <Link
                to="/truth-blocks"
                className="text-emerald-400 hover:text-emerald-200 underline-offset-2 hover:underline uppercase tracking-[0.22em]"
              >
                verify chain →
              </Link>
              <DisputeBtn
                targetType="block_leaf"
                targetId={submission.id}
                targetLabel={submission.claim}
                disputeCount={submission.dispute_count || 0}
                compact
                testidPrefix={`submission-dispute-${submission.id}`}
              />
            </div>
          ) : submission.status === "verified" && submission.triangulation ? (
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono">
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-amber-500/50 text-amber-300 bg-amber-500/[0.05] uppercase tracking-[0.22em]"
                data-testid={`submission-pending-pill-${submission.id}`}
              >
                <Hash className="h-3 w-3" /> queued for next seal
              </span>
              <DisputeBtn
                targetType="submission"
                targetId={submission.id}
                targetLabel={submission.claim}
                disputeCount={submission.dispute_count || 0}
                compact
                testidPrefix={`submission-dispute-${submission.id}`}
              />
            </div>
          ) : null}
        </div>
      )}
    </article>
  );
}

function ArchitectReviewRow({ submission, onAction }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [regrounding, setRegrounding] = useState(false);
  const [tri, setTri] = useState(submission.triangulation);
  const [atoms, setAtoms] = useState(submission.atoms || []);
  useEffect(() => { setTri(submission.triangulation); }, [submission.triangulation]);
  useEffect(() => { setAtoms(submission.atoms || []); }, [submission.atoms]);
  const reground = async () => {
    setRegrounding(true);
    try {
      const res = await regroundSubmission(submission.id);
      const fresh = res?.triangulation || res?.submission?.triangulation;
      if (fresh) setTri(fresh);
      const count = (fresh?.grounding?.sources || []).length;
      toast.success(`Re-ran grounding · ${count} source${count === 1 ? "" : "s"}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || err?.message || "Re-grounding failed");
    } finally {
      setRegrounding(false);
    }
  };
  const verdict = async (decision) => {
    setBusy(true);
    try {
      const res = await verifySubmission(submission.id, decision, note);
      if (decision === "confirmed" && res.submitter_apt_reward != null) {
        const c = res.atoms_confirmed ?? 0;
        const t = res.atoms_total ?? 0;
        const b = res.atoms_bounty ?? 0;
        let line = `Confirmed · +${res.submitter_apt_reward.toFixed(2)} APT minted`;
        if (t > 0) line += ` · ${c}/${t} atoms`;
        if (b > 0) line += ` · ${b} bounty open`;
        toast.success(line);
      } else {
        toast.success(`Marked ${decision}`);
      }
      onAction?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(
        typeof d === "string"
          ? d
          : d?.message || err?.message || "Verification failed"
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <article
      data-testid={`architect-review-${submission.id}`}
      className="border border-sky-500/30 bg-sky-500/[0.03] p-4"
    >
      <div className="flex flex-wrap items-start gap-2 mb-2">
        <span
          className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border font-mono ${
            submission.claim_type === "truth"
              ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
              : "border-rose-500/40 text-rose-300 bg-rose-500/5"
          }`}
        >
          user claims · {submission.claim_type}
        </span>
        <StatusPill status={submission.status} />
        <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-mono">
          {new Date(submission.created_at).toLocaleString()}
        </span>
      </div>
      <div className="text-sm text-zinc-100 leading-snug">
        {submission.claim}
      </div>
      {submission.note && (
        <div className="text-[11px] italic text-zinc-400 mt-2">
          &ldquo;{submission.note}&rdquo;
        </div>
      )}
      {submission.sources && submission.sources.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {submission.sources.map((s, i) => (
            <a
              key={i}
              href={s.startsWith("http") ? s : `https://${s}`}
              target="_blank"
              rel="noreferrer noopener"
              className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80 hover:text-amber-200 font-mono border border-zinc-800 px-1.5 py-0.5"
            >
              source [{i + 1}]
            </a>
          ))}
        </div>
      )}
      {tri && (
        <TriangulationCard
          tri={tri}
          isArchitect
          onReground={reground}
          regrounding={regrounding}
        />
      )}
      {atoms.length > 0 && (
        <AtomList
          atoms={atoms}
          submissionId={submission.id}
          isArchitect
          onAtomChange={(next) => setAtoms(next)}
        />
      )}
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={600}
          placeholder="audit note (optional)"
          data-testid={`architect-note-${submission.id}`}
          className="flex-1 bg-black/40 border border-zinc-800 focus:border-amber-500/60 px-3 py-2 text-xs text-zinc-200 outline-none font-mono"
        />
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => verdict("confirmed")}
            disabled={busy}
            data-testid={`architect-confirm-${submission.id}`}
            className="px-3 py-2 text-[10px] uppercase tracking-[0.25em] border border-emerald-500/60 text-emerald-200 hover:bg-emerald-500/15 disabled:opacity-40 font-mono inline-flex items-center gap-1.5"
          >
            <Check className="h-3.5 w-3.5" /> confirm · mint
          </button>
          <button
            type="button"
            onClick={() => verdict("opinion")}
            disabled={busy}
            data-testid={`architect-opinion-${submission.id}`}
            title="Mark as personal opinion / unverifiable — no mint, no chain anchor"
            className="px-3 py-2 text-[10px] uppercase tracking-[0.25em] border border-violet-500/50 text-violet-300 hover:bg-violet-500/10 disabled:opacity-40 font-mono inline-flex items-center gap-1.5"
          >
            <HelpCircleIcon className="h-3.5 w-3.5" /> opinion
          </button>
          <button
            type="button"
            onClick={() => verdict("rejected")}
            disabled={busy}
            data-testid={`architect-reject-${submission.id}`}
            className="px-3 py-2 text-[10px] uppercase tracking-[0.25em] border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40 font-mono inline-flex items-center gap-1.5"
          >
            <X className="h-3.5 w-3.5" /> reject
          </button>
        </div>
      </div>
    </article>
  );
}


export default function Submit() {
  const { user } = useAuth();
  const [claim, setClaim] = useState("");
  const [claimType, setClaimType] = useState("truth");
  const [submitting, setSubmitting] = useState(false);
  const [mySubs, setMySubs] = useState([]);
  const [pendingSubs, setPendingSubs] = useState([]);
  const [membership, setMembership] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const isArchitect = !!user?.is_architect;

  useEffect(() => {
    const promises = [
      getMySubmissions(50).catch(() => ({ submissions: [] })),
      getMembership().catch(() => null),
    ];
    if (isArchitect) {
      promises.push(getPendingSubmissions(100).catch(() => ({ submissions: [] })));
    }
    Promise.all(promises).then((results) => {
      setMySubs(results[0].submissions || []);
      setMembership(results[1]);
      if (isArchitect && results[2]) {
        setPendingSubs(results[2].submissions || []);
      }
    });
  }, [refreshTick, isArchitect]);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!user?.user_id || (user.user_id || "").startsWith("anon_")) {
      toast.error("Sign in (Google) first so we can credit your AEGIS reward.");
      return;
    }
    const cleanedClaim = claim.trim();
    if (cleanedClaim.length < 8) {
      toast.error("Claim must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createSubmission({
        claim: cleanedClaim,
        claim_type: claimType,
        sources: [],
        note: null,
      });
      toast.success("Submitted · AI triangulation running…");
      setClaim("");
      setMySubs((prev) => [res.submission, ...prev]);
      setRefreshTick((t) => t + 1);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(
        typeof d === "string"
          ? d
          : d?.message || err?.message || "Submission failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isSignedIn = !!user?.user_id && !user.user_id.startsWith("anon_");
  const quotaRemaining = membership?.unbounded ? "∞" : membership?.remaining ?? "?";

  return (
    <div className="min-h-[calc(100vh-130px)] bg-[#09090b] text-zinc-100">
      <LiveTicker fallbackMultiplier={null} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-2 inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> △ submit truth · earn AEGIS
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.1em] text-zinc-100 leading-tight">
            Surface a truth (or expose a lie).
          </h1>
          <p className="text-zinc-400 text-sm sm:text-base mt-3 leading-relaxed">
            Submit a claim you can vouch for. The{" "}
            <span className="text-emerald-300">Sentinel</span> cross-references
            it against live sources and its own truth graph, then replies with
            what it found. If the Architect confirms, you&rsquo;ll receive
            AEGIS tokens for surfacing it.
          </p>
        </div>

        {/* Quota strip */}
        {membership && (
          <div className="mt-5 inline-flex flex-wrap items-center gap-2 border border-zinc-800 bg-[#0b0b0d] px-3 py-2 font-mono text-[11px]" data-testid="submit-quota">
            <span className="uppercase tracking-[0.3em] text-zinc-500 text-[9px]">
              your tier
            </span>
            <span
              className={`px-1.5 py-0.5 uppercase tracking-[0.25em] text-[9px] border ${
                membership.tier === "builder"
                  ? "border-amber-500/50 text-amber-300"
                  : membership.tier === "architect"
                  ? "border-sky-500/50 text-sky-300"
                  : "border-emerald-500/40 text-emerald-300"
              }`}
            >
              {membership.tier}
            </span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-300">
              {quotaRemaining}/{membership.limit ?? "∞"} questions left today
            </span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-500 text-[9px] uppercase tracking-[0.2em]">
              1 submission = 1 question
            </span>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={onSubmit}
          className="mt-6 border border-amber-500/40 bg-amber-500/[0.03] p-5 sm:p-6 space-y-4"
          data-testid="submit-form"
        >
          {/* claim type toggle */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mr-1">
              I&apos;m claiming this is:
            </span>
            <button
              type="button"
              data-testid="submit-type-truth"
              onClick={() => setClaimType("truth")}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-mono border ${
                claimType === "truth"
                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                  : "border-zinc-800 text-zinc-400 hover:border-emerald-500/40"
              }`}
            >
              <Check className="h-3 w-3 inline mr-1" /> a truth
            </button>
            <button
              type="button"
              data-testid="submit-type-lie"
              onClick={() => setClaimType("lie")}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-mono border ${
                claimType === "lie"
                  ? "border-rose-500/60 bg-rose-500/10 text-rose-200"
                  : "border-zinc-800 text-zinc-400 hover:border-rose-500/40"
              }`}
            >
              <X className="h-3 w-3 inline mr-1" /> a lie (to refute)
            </button>
          </div>

          {/* claim text */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
              The claim
            </label>
            <textarea
              value={claim}
              onChange={(e) => setClaim(e.target.value)}
              maxLength={600}
              placeholder='e.g. "Pluto was reclassified as a dwarf planet by the IAU in August 2006."'
              rows={3}
              data-testid="submit-claim"
              className="mt-1 w-full bg-black/40 border border-zinc-800 focus:border-amber-500/60 px-3 py-2 text-sm text-zinc-100 outline-none resize-none"
            />
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 mt-1 font-mono">
              {claim.length}/600
            </div>
          </div>

          {/* submit */}
          <button
            type="submit"
            disabled={submitting || !claim.trim() || !isSignedIn}
            data-testid="submit-confirm"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.3em] border border-amber-500/60 text-amber-200 hover:bg-amber-500/15 hover:text-amber-100 disabled:opacity-40 disabled:cursor-not-allowed font-mono"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> submitting…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" /> submit for verification
              </>
            )}
          </button>
          {!isSignedIn && (
            <p className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-mono flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3" />
              Sign in (Google) to submit — we credit the AEGIS reward to your account.
            </p>
          )}
        </form>

        {/* Architect-only · pending review queue */}
        {isArchitect && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-xl tracking-[0.1em] text-zinc-100 inline-flex items-center gap-2">
                <Shield className="h-4 w-4 text-sky-400" /> Architect · pending review
              </h2>
              <span className="text-[10px] uppercase tracking-[0.25em] text-sky-400 font-mono">
                {pendingSubs.length} in queue
              </span>
            </div>
            {pendingSubs.length === 0 ? (
              <div
                className="border border-dashed border-zinc-800 bg-[#0b0b0d] p-6 text-center text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono"
                data-testid="architect-pending-empty"
              >
                Inbox clear — no submissions waiting.
              </div>
            ) : (
              <div className="space-y-3" data-testid="architect-pending-list">
                {pendingSubs.map((s) => (
                  <ArchitectReviewRow
                    key={s.id}
                    submission={s}
                    onAction={() => setRefreshTick((t) => t + 1)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* My submissions */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-xl tracking-[0.1em] text-zinc-100">
              My submissions
            </h2>
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
              {mySubs.length} total
            </span>
          </div>
          <StatusLegend />
          {mySubs.length === 0 ? (
            <div
              className="border border-dashed border-zinc-800 bg-[#0b0b0d] p-6 text-center text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono"
              data-testid="submit-empty"
            >
              No submissions yet. Send your first truth above.
            </div>
          ) : (
            <div className="space-y-2" data-testid="my-submissions">
              {mySubs.map((s) => (
                <SubmissionRow
                  key={s.id}
                  submission={s}
                  onRefresh={() => setRefreshTick((t) => t + 1)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
